import React, { useCallback, useId } from "react";
import styled, { useTheme } from "styled-components/native";
import { Dimensions, Modal, Platform, TouchableOpacity } from "react-native";
import { ModalProps } from "@components/atoms/SlideSheet/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "..";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "../Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import Animated, {
  withTiming,
  useSharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { css } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { DefaultTheme } from "styled-components/native";
import { useEscapeKey } from "@hooks/accessibility";
import { getEmbeddedModalMaxHeight } from "@utils/iframeMessaging";

const SlideSheet = ({
  headerTitle,
  children,
  onClose,
  isOpen,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  minModalHeight,
  backgroundColor,
  skipKeyboardAware,
  fillContentHeight,
  allowFullScreen,
  height = "auto",
  clearChat = false,
  handleClearChat = () => {},
  leftIcon,
  leftIconPress,
}: ModalProps) => {
  const typography = useTypographyStore();
  const handleCloseModal = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const theme = useTheme();
  const userInfo = useUserInfo();
  const windowHeight = Dimensions.get(
    allowFullScreen ? "screen" : "window"
  ).height;
  // Window height inside the iframe is the page height. Keep the sheet within
  // 85% of the device screen.
  const embeddedMaxHeight = getEmbeddedModalMaxHeight();
  const sheetFillsFrame =
    !!embeddedMaxHeight && typeof height === "string" && height.endsWith("%");
  const sheetHeight = sheetFillsFrame ? `${embeddedMaxHeight}px` : height;
  const sheetMaxHeight = embeddedMaxHeight ?? windowHeight * 0.9;
  const { deviceWidth: width, isFold } = useIsFoldable();
  const opacity = useSharedValue(isOpen ? 1 : 0);
  const translateY = useSharedValue(isOpen ? 0 : windowHeight);

  // Generate unique ID for ARIA labeling
  const modalId = useId();
  const titleId = `${modalId}-title`;

  // Handle Escape key for web (tablet with keyboard)
  useEscapeKey({
    isActive: isOpen,
    onEscape: handleCloseModal,
  });

  React.useEffect(() => {
    opacity.value = withTiming(isOpen ? 1 : 0, { duration: 300 });
    translateY.value = withTiming(isOpen ? 0 : windowHeight, { duration: 300 });
  }, [isOpen]);

  const backgroundStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const overlayStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isOpen}
      supportedOrientations={["portrait", "landscape"]}
    >
      <BackgroundAnimated
        style={backgroundStyle}
        iFrame={userInfo.isIFrame()}
      />
      <OverlayAnimated style={overlayStyle}>
        <ModalBox
          backgroundColor={backgroundColor}
          height={sheetHeight}
          minModalHeight={minModalHeight}
          windowHeight={sheetMaxHeight}
          windowWidth={width}
          isFold={isFold}
          iFrame={userInfo.isIFrame() || !!embeddedMaxHeight}
          accessibilityViewIsModal={true}
          accessible={true}
          accessibilityRole="none"
        >
          <OverlapContainer>
            <Header>
              <VDBText
                textAlign="center"
                type={typography["H2-M"]}
                color={theme["core-05"]}
                nativeID={titleId}
                accessibilityRole="header"
              >
                {headerTitle}
              </VDBText>
            </Header>

            <ButtonContainer clearChat={clearChat}>
              {leftIcon && (
                <TouchableOpacity onPress={leftIconPress}>
                  <VDBIcon
                    name={leftIcon}
                    variant={IconsVariant.VDBCustom}
                    size={24}
                    color={theme["core-06"]}
                  />
                </TouchableOpacity>
              )}
              {clearChat && (
                <TouchableOpacity
                  onPress={handleClearChat}
                  accessibilityRole="button"
                  accessibilityLabel="Clear chat"
                >
                  <VDBText
                    textAlign="center"
                    type={typography["H2-M"]}
                    color={theme["core-05"]}
                  >
                    Clear Chat
                  </VDBText>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onPress={handleCloseModal}
                accessibilityRole="button"
                accessibilityLabel="Close modal"
              >
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={24}
                  color={theme["core-06"]}
                />
              </TouchableOpacity>
            </ButtonContainer>
          </OverlapContainer>

          <LineView />
          <ScrollViewContainer
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            paddingBottom={paddingBottom}
            paddingTop={paddingTop}
            height={sheetFillsFrame ? "auto" : sheetHeight}
            iFrame={userInfo.isIFrame()}
          >
            {!skipKeyboardAware ? (
              <KeyboardAwareScrollView
                bottomOffset={20}
                keyboardShouldPersistTaps="always"
              >
                {children}
              </KeyboardAwareScrollView>
            ) : (
              <FullModal fillContentHeight={fillContentHeight}>
                {children}
              </FullModal>
            )}
          </ScrollViewContainer>
        </ModalBox>
      </OverlayAnimated>
    </Modal>
  );
};

const FullModal = styled.View<{ fillContentHeight?: boolean }>`
  ${({ fillContentHeight }: { fillContentHeight?: boolean }) =>
    fillContentHeight ? `flex: 1;` : `height: 100%;`}
  ${Platform.OS === "web" &&
  css`
    min-height: 0;
  `}
`;

const OverlayAnimated = styled(Animated.View)`
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
`;

const BackgroundAnimated = styled(Animated.View)<{ iFrame?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
    `}
`;

const ModalBox = styled.View<{
  backgroundColor?: string;
  windowHeight?: any;
  height?: any;
  minModalHeight?: number;
  windowWidth?: any;
  isFold?: any;
  iFrame?: boolean;
}>`
  background-color: ${({
    theme,
    backgroundColor,
  }: {
    theme: DefaultTheme;
    backgroundColor?: string;
  }) => (backgroundColor ? backgroundColor : theme["core-01"])};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  height: ${({ height }: { height?: string }) => `${height}`};
  min-height: ${({
    height,
    minModalHeight,
  }: {
    height?: string;
    minModalHeight?: number;
  }) => (minModalHeight ? `${minModalHeight}px` : `${height}`)};
  max-height: ${({ windowHeight }: { windowHeight?: number }) =>
    windowHeight ? `${windowHeight}px` : "90%"};
  ${({ windowWidth, isFold }: { windowWidth?: number; isFold?: boolean }) =>
    isFold &&
    css`
      width: ${windowWidth}px;
      align-self: center;
    `}
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
      overflow: hidden;
    `}
  ${Platform.OS === "web" &&
  css`
    display: flex;
    flex-direction: column;
  `}
`;

const OverlapContainer = styled.View`
  position: relative;
  flex-direction: row;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const Header = styled.View<any>`
  flex: 1;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
  padding-bottom: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
`;

const ButtonContainer = styled.View<{ clearChat?: boolean }>`
  position: absolute;
  top: 1px;
  right: 2px;
  margin: ${SpaceType.Extra}px;
  ${({ clearChat }) =>
    clearChat &&
    `flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: ${SpaceType.Basic}px;`}
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const ScrollViewContainer = styled.View<{
  height?: any;
  paddingLeft?: any;
  paddingRight?: any;
  paddingTop?: any;
  paddingBottom?: any;
  iFrame?: boolean;
}>`
  flex: 1;
  flex-basis: auto;
  height: ${({ height }: { height?: string }) => `${height}`};
  ${({ paddingLeft }: { paddingLeft?: number }) =>
    paddingLeft ? `padding-left: ${paddingLeft}px;` : `padding-left: 0px;`}
  ${({ paddingRight }: { paddingRight?: number }) =>
    paddingRight ? `padding-right: ${paddingRight}px;` : `padding-right: 0px;`}
  ${({ paddingTop }: { paddingTop?: number }) =>
    paddingTop ? `padding-top: ${paddingTop}px;` : `padding-top: 0px;`}
  ${({ paddingBottom }: { paddingBottom?: number }) =>
    paddingBottom
      ? `padding-bottom: ${paddingBottom}px;`
      : `padding-bottom: 0px;`}
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
    `}
  ${Platform.OS === "web" &&
  css`
    min-height: 0;
  `}
`;

export default SlideSheet;
